import React, { useState, useEffect } from "react";
import { salaryAPI, userAPI } from "../../lib/api";
import { toast } from "sonner";
import { formatDate, formatCurrency } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Plus, DollarSign, Loader2, Trash2 } from "lucide-react";

const SalaryStructuresPage = () => {
  const [structures, setStructures] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [formData, setFormData] = useState({
    user_id: "",
    basic_salary: 0,
    effective_from: "",
    components: [],
  });
  const [newComponent, setNewComponent] = useState({
    name: "",
    type: "earning",
    amount: 0,
    is_percentage: false,
    percentage_of: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [structuresRes, usersRes] = await Promise.all([
        salaryAPI.getStructures(),
        userAPI.getAll(),
      ]);
      setStructures(structuresRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const addComponent = () => {
    if (!newComponent.name || newComponent.amount <= 0) {
      toast.error("Please fill component details");
      return;
    }
    setFormData({
      ...formData,
      components: [...formData.components, { ...newComponent }],
    });
    setNewComponent({
      name: "",
      type: "earning",
      amount: 0,
      is_percentage: false,
      percentage_of: "",
    });
  };

  const removeComponent = (index) => {
    setFormData({
      ...formData,
      components: formData.components.filter((_, i) => i !== index),
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    try {
      await salaryAPI.createStructure(formData);
      toast.success("Salary structure created");
      setShowCreateDialog(false);
      setFormData({
        user_id: "",
        basic_salary: 0,
        effective_from: "",
        components: [],
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create structure");
    } finally {
      setCreateLoading(false);
    }
  };

  const getUserName = (userId) => {
    const user = users.find((u) => u.id === userId);
    return user ? `${user.first_name} ${user.last_name}` : "-";
  };

  const calculateTotal = (structure) => {
    let total = structure.basic_salary;
    structure.components?.forEach((comp) => {
      const amount = comp.is_percentage
        ? structure.basic_salary * (comp.amount / 100)
        : comp.amount;
      if (comp.type === "earning") {
        total += amount;
      } else {
        total -= amount;
      }
    });
    return total;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="salary-structures-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Salary Structures</h1>
          <p className="text-slate-500">Manage employee salary structures</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="create-structure-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Structure
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Create Salary Structure</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="form-group">
                <Label>Employee</Label>
                <Select
                  value={formData.user_id}
                  onValueChange={(value) => setFormData({ ...formData, user_id: value })}
                >
                  <SelectTrigger data-testid="structure-user-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <Label>Basic Salary</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.basic_salary}
                    onChange={(e) => setFormData({ ...formData, basic_salary: parseFloat(e.target.value) })}
                    required
                    data-testid="structure-basic-salary"
                  />
                </div>
                <div className="form-group">
                  <Label>Effective From</Label>
                  <Input
                    type="date"
                    value={formData.effective_from}
                    onChange={(e) => setFormData({ ...formData, effective_from: e.target.value })}
                    required
                    data-testid="structure-effective-date"
                  />
                </div>
              </div>

              {/* Components */}
              <div className="space-y-3">
                <Label>Salary Components</Label>
                {formData.components.map((comp, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                    <span className="flex-1 text-sm">
                      {comp.name}: {comp.is_percentage ? `${comp.amount}%` : formatCurrency(comp.amount)}
                    </span>
                    <Badge variant={comp.type === "earning" ? "default" : "destructive"}>
                      {comp.type}
                    </Badge>
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeComponent(index)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}

                {/* Add Component */}
                <div className="p-3 border rounded-lg space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Component name"
                      value={newComponent.name}
                      onChange={(e) => setNewComponent({ ...newComponent, name: e.target.value })}
                    />
                    <Select
                      value={newComponent.type}
                      onValueChange={(value) => setNewComponent({ ...newComponent, type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="earning">Earning</SelectItem>
                        <SelectItem value="deduction">Deduction</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="number"
                      min="0"
                      placeholder="Amount"
                      value={newComponent.amount || ""}
                      onChange={(e) => setNewComponent({ ...newComponent, amount: parseFloat(e.target.value) || 0 })}
                    />
                    <Button type="button" variant="outline" onClick={addComponent}>
                      Add Component
                    </Button>
                  </div>
                </div>
              </div>

              <Button type="submit" className="w-full btn-primary" disabled={createLoading} data-testid="submit-structure">
                {createLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Structure"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Structures Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Basic Salary</TableHead>
                <TableHead>Components</TableHead>
                <TableHead>Net Salary</TableHead>
                <TableHead>Effective From</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {structures.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    No salary structures found
                  </TableCell>
                </TableRow>
              ) : (
                structures.map((structure) => (
                  <TableRow key={structure.id} data-testid={`structure-row-${structure.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                          <DollarSign className="w-5 h-5 text-emerald-600" />
                        </div>
                        <span className="font-medium">{getUserName(structure.user_id)}</span>
                      </div>
                    </TableCell>
                    <TableCell>{formatCurrency(structure.basic_salary)}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {structure.components?.slice(0, 3).map((comp, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {comp.name}
                          </Badge>
                        ))}
                        {structure.components?.length > 3 && (
                          <Badge variant="secondary" className="text-xs">
                            +{structure.components.length - 3}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium text-emerald-600">
                      {formatCurrency(calculateTotal(structure))}
                    </TableCell>
                    <TableCell>{formatDate(structure.effective_from)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default SalaryStructuresPage;
